import { __, compose, divide, is, map, path, when } from 'ramda'
import httpCacheItem from './container/HttpCacheItem'

const pathFunc = path
const isObject = is(Object)
const isArray = is(Array)
const isCacheItem = (value) => (isObject(value) && value instanceof httpCacheItem)
const getEpoch = () => compose(Number, Math.floor, divide(__, 1000))((new Date()).getTime())

/**
 * Core HTTP cache routine runner.
 */
export default class HttpCache {
  /**
   * Constructor.
   *
   * @param {HttpCachePoolConfigProvider} poolConfigProvider
   * @param {HttpCacheAdapterFactory} adapterFactory
   * @param {HttpCacheServiceFactory} serviceFactory
   * @param {function} expiredItemFilter
   * @param {function} createdAfterFilter
   */
  constructor(poolConfigProvider, adapterFactory, serviceFactory, expiredItemFilter, createdAfterFilter) {
    this.poolConfigProvider = poolConfigProvider
    this.adapterFactory = adapterFactory
    this.serviceFactory = serviceFactory
    this.expiredItemFilter = expiredItemFilter
    this.createdAfterFilter = createdAfterFilter
  }

  setVuexStore(vuexStore) {
    this.vuexStore = vuexStore
  }

  /**
   * Perform the read operation against the cache.
   *
   * @param {String} path
   * @param {Object} parameters
   * @param {string|null} earliestPermittedHit
   * @return {Object}
   */
  read(path, parameters = {}, earliestPermittedHit = null) {
    const cookieDecision = pathFunc(['vuexStore', 'getters', 'cookieConsent/decision'], this)

    // Get pool config. Quit if null
    const poolConfig = this.poolConfigProvider.provide(path, parameters, cookieDecision)
    if (poolConfig === null) {
      return { result: null }
    }

    // Load all cache items from the adapter.
    const adapter = this.adapterFactory.create(poolConfig)
    const initialCachedItems = adapter.load(poolConfig)

    // Filter the list to remove any expired items. Write updated list to the cache if any are removed.
    let cachedItems = this.expiredItemFilter(getEpoch(), initialCachedItems)
    if (initialCachedItems.length !== cachedItems.length) {
      adapter.save(poolConfig, cachedItems)
    }

    if (earliestPermittedHit) {
      cachedItems = this.createdAfterFilter(earliestPermittedHit, cachedItems)
    }

    const service = this.serviceFactory.create(poolConfig)
    const hits = service.read({ path, parameters, poolConfig }, cachedItems)

    return {
      result: compose(
        when(isCacheItem, pathFunc(['value'])),
        when(isArray, map(when(isCacheItem, pathFunc(['value'])))),
      )(hits),
      isPartialResult: service.isPartialResult(),
    }
  }

  /**
   * Perform the write action against the cache.
   *
   * @param {String} path
   * @param {Object} parameters
   * @param {*} value
   * @return {Boolean}
   */
  write(path, parameters = {}, value = null) {
    const cookieDecision = pathFunc(['vuexStore', 'getters', 'cookieConsent/decision'], this)

    // Get pool config. Quit if null
    const poolConfig = this.poolConfigProvider.provide(path, parameters, cookieDecision)
    if (poolConfig === null) {
      return false
    }

    const currentTimestamp = getEpoch()

    // Load all cache items from the adapter and pre-filter for those that are expired.
    const adapter = this.adapterFactory.create(poolConfig)
    let cachedItems = this.expiredItemFilter(currentTimestamp, adapter.load(poolConfig))

    // Pass to the write method against the service
    cachedItems = this.serviceFactory.create(poolConfig).write(
      { path, parameters, poolConfig, currentTimestamp },
      cachedItems,
      value,
    )

    // Save the updated list
    return adapter.save(poolConfig, cachedItems)
  }
}
